'use strict';
import {
  Component, Input, Output, ElementRef, ViewChild, EventEmitter,
  OnChanges, OnInit, SimpleChanges, OnDestroy
} from '@angular/core';
import { find, groupBy, get as _get } from 'lodash';
import { Observable, Subscription } from 'rxjs';

const findIndex = function (arr, el) {
  return Array.prototype.indexOf.call(arr, el);
};
const includes = function (arr, el) {
  return findIndex(arr, el) !== -1;
};

@Component({
  selector: 'dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent implements OnChanges, OnInit, OnDestroy {
  @Input() open = false;
  menuHeight = '0px';
  menuWidth = '100%';
  autocompleteTerm;
  searchTerm;
  keySearchTerm = '';
  searching = false;
  keyTimeout;
  groups = {};
  groupKeys;
  buttonText = '';
  toggleClick: Subscription;
  refreshSubscription: Subscription;
  @ViewChild('optionsEl', { static: false }) optionsElRef: ElementRef;
  @Input() selectedOption;
  @Input() selectedItems = [];
  @Input('title') title;
  @Input('icon') icon;
  @Input() setTitle = true;
  @Input() changeTitle = true;
  @Input() config;
  @Input() loading = false;
  @Input() options = [];
  @Input() backgroundColor = 'rgba(255, 255, 255, 0.15)';
  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() nextPage: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSearch: EventEmitter<any> = new EventEmitter<any>();
  @Input() disabled;
  @Input() blur = true;
  @Input() toggle = false;
  @Input() refreshObservable$: Observable<boolean>;

  constructor(private el: ElementRef, private _eref: ElementRef) {
    this.config = this.config || {};
  }

  ngOnInit() {
    this.config = this.config || {};
    this.config = {
      nameProperty: 'name',
      subtitleProperty: 'subtitle',
      subtitle: false,
      disableSubtitleOnSelect: false,
      imageProperty: 'image',
      image: false,
      multiselect: false,
      deselectOption: null,
      selectedProp: 'selected',
      style: '',
      pin: '',
      groupBy: 'group',
      group: false,
      selectable: true,
      rightAlign: false,
      autocomplete: false,
      showLoading: false,
      loadingOptions: false,
      optionIcon: false,
      iconProperty: 'icon',
      numbered: false,
      ...this.config
    };

    if (!Array.isArray(this.options)) {
      throw { error: 'dropdown options must be an array.', dropdown: this };
    }
    this.setSelectedOption();
    this.setButtonText();

    if (this.open) { this.menuHeight = 'auto'; }
    if (this.refreshObservable$) {
      this.refreshSubscription = this.refreshObservable$.subscribe(() => {
        this.deselectAll();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.config && this.config.group) {
      this.groups = this.config.groupProperty ?
        groupBy(this.options, this.config.groupProperty) :
        groupBy(this.options, this.config.groupBy);
    }
    this.groupKeys = Object.keys(this.groups);
    this.setButtonText();
  }

  ngOnDestroy() {
    if (this.toggleClick && typeof this.toggleClick.unsubscribe === 'function') {
      this.toggleClick.unsubscribe();
    }
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  public setSelectedOption() {
    if (this.options && Array.isArray(this.options)) {
      if (!this.config.multiselect) {
        this.selectedOption = this.options.find(option => {
          return option && (option[this.config.selectedProp] || (option.id && option.id === this.getIdValue(this.selectedOption)));
        }) || this.selectedOption;
        this.autocompleteTerm = this.config.autocomplete ? this.selectedOption[this.config.nameProperty] : null;
      }
    }
    if (
      this.config && this.config.multiselect && this.selectedItems &&
      Array.isArray(this.selectedItems) && this.selectedItems.length > 0
    ) {
      this.selectedItems.forEach(item => {
        this.options.forEach(option => {
          if (option.id === item.id) {
            return option[this.config.selectedProp] = true;
          }
        });
      });
    }
  }

  onKeyPress(e) {
    if (
      this.el && this.el.nativeElement &&
      this.el.nativeElement.contains(document.activeElement) &&
      !this.config.autocomplete
    ) {
      if (e.code === 'Escape' && this.open) {
        this.toggleMenu();
      }
      let options = this.optionsElRef && this.optionsElRef.nativeElement &&
        this.optionsElRef.nativeElement.children &&
        this.optionsElRef.nativeElement.children.length &&
        this.optionsElRef.nativeElement.children[0].children;
      if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        if (!this.open) {
          this.toggleMenu();
        } else if (options.length) {
          let lastOption = options[options.length - 1];
          let index = findIndex(options, document.activeElement);
          if (e.code === 'ArrowDown') {
            if (!includes(options, document.activeElement)) {
              options[0].focus();
            } else if (document.activeElement !== lastOption) {
              options[index + 1].focus();
            }
          } else if (e.code === 'ArrowUp') {
            if (!includes(options, document.activeElement)) {
              lastOption.focus();
            } else if (index !== 0) {
              options[index - 1].focus();
            }
          }
        }
        e.preventDefault();
      } else if (e.key.match(/^\w$/) && e.target.nodeName.toLowerCase() !== 'input') {
        if (this.keyTimeout) {
          clearTimeout(this.keyTimeout);
        }
        this.keyTimeout = setTimeout(() => {
          this.keySearchTerm = '';
        }, 1000);
        this.keySearchTerm += e.key.toLowerCase();
        let option: any = find(options, (o: HTMLElement) => {
          return o.innerText.toLowerCase().match(RegExp('^' + this.keySearchTerm));
        });
        if (option) {
          option.focus();
        }
      }
    }
    setTimeout(() => {
      if (this.open && !this.el.nativeElement.contains(document.activeElement)) {
        this.toggleMenu();
      }
    }, 0);
  }
  buttonKeyPress(e) {
    if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault();
      this.toggleMenu();
    }
  }
  optionKeyPress(e, option) {
    if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault();
      this.toggleOption(option);
    }
  }
  toggleMenu() {
    if (!this.disabled) {
      this.open = !this.open;
    }

    if (this.blur) {
      if (this.open) {
        this.toggleClick = Observable.fromEvent(document, 'click').subscribe((e: MouseEvent) => {
          if (!this._eref.nativeElement.contains(e.target) && this.open) { this.toggleMenu(); }
        });
      } else if (this.toggleClick && typeof this.toggleClick.unsubscribe === 'function') {
        this.toggleClick.unsubscribe();
      }
    }

    this.searching = false;
    this.setSize();
  }

  setSize() {
    if (this.optionsElRef && this.optionsElRef.nativeElement) {
      const menu = this.optionsElRef.nativeElement;
      this.menuHeight = (this.open ? menu.offsetHeight : 0) + 'px';
      this.menuWidth = this.optionsElRef.nativeElement.offsetWidth + 'px';

      const rect = menu.getBoundingClientRect();
      if (rect.height + menu.parentElement.getBoundingClientRect().top > window.innerHeight) {
        menu.parentElement.style.transform = 'translateY(-' +
          (rect.height + menu.parentElement.getBoundingClientRect().top - window.innerHeight) + 'px)';
      } else if (!this.open) {
        menu.parentElement.style.transform = 'opacity(0)';
        menu.parentElement.style.transition = 'opacity 2s linear';
      } else if (this.open) {
        menu.parentElement.style.transform = 'opacity(1)';
        menu.parentElement.style.transition = 'opacity 2s linear';
      }
    }
  }

  toggleOption(option, toggle = true) {
    if (option && !option.disabled) {
      if (this.config.multiselect) {
        option[this.config.selectedProp] = !option[this.config.selectedProp];
        this.selectedItems = this.options.filter((_option) => { return _option[this.config.selectedProp]; });
        this.onSelect.emit(this.selectedItems);
      } else {
        this.deselectAll();
        this.selectedOption = option;
        this.onSelect.emit(this.selectedOption);
        if (toggle) { this.toggleMenu(); }
        this.autocompleteTerm = this.config.autocomplete ? this.selectedOption[this.config.nameProperty] : null;
      }
      this.setButtonText();
    }
  }
  isSelected(option) {
    if (!this.config.multiselect) {
      return (!option || !option.button) && (option && option[this.config.selectedProp] || option === this.selectedOption);
    } else {
      return option && option[this.config.selectedProp];
    }
  }
  public deselectAll() {
    this.options.forEach((option) => {
      if (typeof option === 'object') {
        option[this.config.selectedProp] = undefined;
      }
    });
    this.selectedOption = undefined;
    if (this.config.multiselect) {
      this.selectedItems.length = 0;
      this.onSelect.emit(this.selectedItems);
    }
    this.setButtonText();
  }

  setButtonText() {
    this.setSelectedOption();
    if (this.config.multiselect) {
      this.buttonText = this.selectedItems && this.selectedItems.length > 0 ?
        this.optionDisplayMultiple(this.selectedItems) :
        this.title;
    } else {
      // single select
      this.buttonText = this.selectedOption ?
        this.optionDisplay(this.selectedOption) +
        (this.config.subtitle && !this.config.disableSubtitleOnSelect ?
          ' ' + this.optionSubtitleDisplay(this.selectedOption) : '') :
        this.title;
    }
    if (!this.setTitle) { this.buttonText = null; }
    if (!this.changeTitle) { this.buttonText = this.title; }
  }

  getIdValue(option) {
    if (typeof option === 'object') {
      return _get(option, 'id');
    } else {
      return option;
    }
  }

  optionDisplay(option) {
    if (typeof option === 'object') {
      return _get(option, this.config.nameProperty);
    } else {
      return option;
    }
  }
  optionDisplayMultiple(items) {
    return Array.isArray(items) ? items.map(item => _get(item, this.config.nameProperty)).join(', ') : null;
  }
  optionSubtitleDisplay(option) {
    if (typeof option === 'object') {
      return _get(option, this.config.subtitleProperty);
    } else {
      return option;
    }
  }
  optionImageDisplay(option) {
    if (typeof option === 'object') {
      return _get(option, this.config.imageProperty);
    } else {
      return option;
    }
  }

  optionIconDisplay(option) {
    if (typeof option === 'object') {
      return _get(option, this.config.iconProperty);
    } else {
      return option;
    }
  }

  changeSearch() {
    this.searching = true;
    this.onSearch.emit(this.searchTerm);
  }
  changeAutocomplete() {
    this.onSearch.emit(this.autocompleteTerm);
    this.deselectAll();
    this.toggleMenu();
  }
  onScroll() {
    this.nextPage.emit();
  }
  windowScroll(e) {
    if (this.open) {
      e.preventDefault();
    }
    return false;
  }
}
